import { Injectable } from '@angular/core';
import { Keyboard } from '@capacitor/keyboard';
import { Platform } from '@ionic/angular/standalone';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { debounceTime, tap, withLatestFrom } from 'rxjs/operators';
import { Question, QuestionItemType, isQuestion } from 'src/app/services/slip.service';
import { upsertQuestionResponse } from 'src/app/store/actions/slip-question-response.action';
import { selectSlipQuestionResponse } from 'src/app/store/selectors/slip-question-response.selector';
import { Status } from './ssqw-form-slide/status';

export interface SlipQuestionsWizardState {
  slipQuestionResponseId: string;
  items: QuestionItemType[];
  itemStatus: Status[];
  currentItemIndex: number;
}

@Injectable()
export class StudentSlipQuestionsWizardStore extends ComponentStore<SlipQuestionsWizardState> {
  constructor(
    private store: Store,
    private platform: Platform
  ) {
    super({
      slipQuestionResponseId: null,
      items: [],
      itemStatus: [],
      currentItemIndex: 0
    });
  }
  readonly setItemStatus = this.updater((state, value: { index: number; status: Status }) => {
    const newStatus = state.itemStatus.map((valueInner, index) => (index === value.index ? value.status : valueInner));
    return {
      ...state,
      itemStatus: newStatus
    };
  });

  readonly setItemIndex = this.updater((state, value: number) => {
    return {
      ...state,
      currentItemIndex: value
    };
  });

  readonly slideItems$ = this.select((state) => {
    {
      let questionNumber = 0;
      return state.items.map((value, index) => {
        if (isQuestion(value)) ++questionNumber;
        return {
          questionNumber: questionNumber,
          item: value,
          value: state.itemStatus[index].value
        };
      });
    }
  });

  readonly slideValues$ = this.select((state) => {
    {
      return state.items.map((value, index) => state.itemStatus[index].value);
    }
  });

  readonly onSummary$ = this.select((state) => {
    {
      return state.currentItemIndex === state.items.length;
    }
  });

  readonly summary$ = this.select((state) => {
    {
      return state.items
        .map((value, index) => ({
          index: index,
          item: value
        }))
        .filter(i => isQuestion(i.item))
        .map(value => ({
          slide: value.index,
          question: value.item as Question,
          status: state.itemStatus[value.index]
        }));
    }
  }).pipe(tap(v => console.log('Wizard Summary: ', v)));

  readonly footerModel$ = this.select((state) => {
    {
      return state.items.filter((item, index) => isQuestion(item) && !state.itemStatus[index].valid).length > 0
        ? {
            valid: false,
            atStart: state.currentItemIndex === 0,
            atEnd: state.currentItemIndex === state.items.length
          }
        : {
            valid: true,
            atStart: state.currentItemIndex === 0,
            atEnd: state.currentItemIndex === state.items.length,
            submitPayload: state.itemStatus.map(s => s.value)
          };
    }
  }).pipe(tap(console.log));

  readonly init = this.effect((params$: Observable<{ slipQuestionResponseId: string; items: QuestionItemType[] }>) => {
    return params$.pipe(
      concatLatestFrom(params => this.store.select(selectSlipQuestionResponse(params.slipQuestionResponseId))),
      tap(([params, r]) => {
        const initial = r?.payload;
        this.patchState({
          slipQuestionResponseId: params.slipQuestionResponseId,
          items: params.items,
          itemStatus: params.items.map((_, index) => ({
            valid: false,
            errors: null,
            value: initial ? initial[index] : null
          }))
        });
      })
    );
  });

  readonly cacheState = this.effect((trigger$) => {
    return trigger$.pipe(
      debounceTime(2000),
      withLatestFrom(this.slideValues$),
      tap(([_, values]) => {
        this.store.dispatch(
          upsertQuestionResponse({
            data: { id: this.get().slipQuestionResponseId, payload: values, modifiedTimestamp: Date.now() }
          })
        );
      })
    );
  });

  // Certain keypads on iOS have no way to dismiss them so use the accessory bar for these
  // Note: Remember to switch of on component destroy
  readonly configureKeyboard = this.effect((trigger$) => {
    return trigger$.pipe(
      tap((_) => {
        if (this.platform.is('ios')) {
          const item = this.get().items[this.get().currentItemIndex];
          if (isQuestion(item)) {
            Keyboard.setAccessoryBarVisible({ isVisible: ['phone', 'long-text'].includes(item.answerType) });
          }
        }
      })
    );
  });
}
